import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function LaMaison() {
  const [heroRef, heroVis] = useReveal(0.1);
  const [txtRef, txtVis] = useReveal(0.15);
  const [sylRef, sylVis] = useReveal(0.15);
  const [sylTxtRef, sylTxtVis] = useReveal(0.2);
  const [quoteRef, quoteVis] = useReveal(0.2);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[70vh] md:h-[80vh] flex items-center justify-center overflow-hidden bg-black">
        <img src="/photos/Sylvie-SMP_1.jpg" alt="Atelier Pernety"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.3) contrast(1.1) saturate(0.8)' }} />
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.4)' }} />

        <div ref={heroRef} className={`relative z-10 text-center px-6 transition-all duration-[1.5s] ${heroVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-10 h-px bg-white/20" />
            <div className="w-1.5 h-1.5 rotate-45 bg-white/40" />
            <div className="w-10 h-px bg-white/20" />
          </div>
          <p className="text-xs tracking-[0.5em] uppercase mb-6 font-light text-white/50">Pernety</p>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light tracking-wider mb-6">
            La Maison
          </h1>
          <p className="text-white/35 text-sm md:text-base max-w-lg mx-auto leading-relaxed font-serif italic">
            Born from Memory
          </p>
        </div>
      </section>

      {/* ── IDENTITE DE MARQUE ── */}
      <section className="py-24 md:py-32 px-6 bg-white">
        <div ref={txtRef} className={`max-w-3xl mx-auto text-center transition-all duration-[1.5s] ${txtVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="w-12 h-px bg-gray-200" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-10 w-auto opacity-20" />
            <div className="w-12 h-px bg-gray-200" />
          </div>

          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-10 leading-tight">
            Des bijoux pensés comme<br />des fragments de mémoire
          </h2>

          <div className="space-y-6 text-gray-500 text-base md:text-lg leading-[1.9] max-w-2xl mx-auto">
            <p>
              Pernety est une maison de joaillerie qui imagine des bijoux pensés comme des fragments de mémoire.
              Chaque création naît d'un souvenir, d'une émotion, d'une lumière particulière,
              d'un instant vécu quelque part dans le monde.
            </p>
            <p>
              Héritage de la terre, nos pierres sont choisies avec soin pour leur caractère unique, leur éclat singulier.
              Chaque bijou devient ainsi une présence intime à porter et à transmettre.
            </p>
          </div>

          <div className="mt-12 flex items-center justify-center gap-6 text-sm tracking-[0.3em] text-gray-300 font-light">
            <span>Paris</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>Bangkok</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>Le Monde</span>
          </div>
        </div>
      </section>

      {/* ── TOUT COMMENCE PAR UNE PIERRE ── */}
      <section className="py-0 bg-black">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 min-h-[60vh]">
          <div className="flex items-center justify-center px-8 md:px-16 py-20">
            <div className="max-w-md">
              <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-white leading-snug mb-8 italic font-light">
                Tout commence<br />par une pierre...
              </p>
              <div className="w-12 h-px bg-white/15 mb-8" />
              <p className="text-white/40 leading-[1.9] text-[15px]">
                Une émotion, une lumière, un souvenir, un instant vécu quelque part deviennent bijou...
              </p>
            </div>
          </div>
          <div className="relative overflow-hidden min-h-[400px]">
            <img src="/photos/Sylvie-SMP_11.jpg" alt="Pierres précieuses"
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.7) contrast(1.1)' }} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
          </div>
        </div>
      </section>

      {/* ── LA CREATRICE ── */}
      <section className="relative py-0 bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[90vh]">
          <div ref={sylRef}
            className={`md:col-span-6 relative overflow-hidden transition-all duration-[1.5s] ${sylVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <img src="/photos/Sylvie-SMP_15.jpg" alt="Sylvie — Fondatrice"
              className="w-full h-[500px] md:h-full object-cover" />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute bottom-8 left-8">
              <p className="font-serif text-white text-2xl tracking-wider">Sylvie</p>
              <p className="text-white/50 text-xs tracking-[0.3em] uppercase mt-1">Gemmologue & Créatrice</p>
            </div>
          </div>

          <div ref={sylTxtRef}
            className={`md:col-span-6 flex items-center px-8 md:px-16 lg:px-20 py-20 md:py-0 transition-all duration-[1.5s] delay-300 ${sylTxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-px bg-gray-900" />
                <p className="text-xs tracking-[0.4em] uppercase font-medium text-gray-400">La Créatrice</p>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-10 leading-tight">
                Là où la pierre<br />rencontre l'émotion
              </h2>

              <div className="space-y-6 mb-12">
                <p className="text-gray-500 leading-[1.9] text-[15px]">
                  Gemmologue, j'ai été formée au Laboratoire Français de Gemmologie à Paris
                  puis à l'Asian Institute of Gemological Studies à Bangkok, où je me suis spécialisée dans les pierres de couleur.
                </p>
                <p className="text-gray-500 leading-[1.9] text-[15px]">
                  Curieuse et passionnée, je continue d'enrichir mes connaissances et d'affiner
                  mon regard sur les gemmes. Mes créations naissent toujours d'une pierre qui m'inspire
                  particulièrement, autour de laquelle le bijou prend forme.
                </p>
              </div>

              <div className="flex gap-12 mb-12 py-8 border-t border-b border-gray-100">
                <div>
                  <p className="font-serif text-2xl text-gray-900">Paris</p>
                  <p className="text-xs text-gray-400 tracking-wider mt-1">Formation</p>
                </div>
                <div>
                  <p className="font-serif text-2xl text-gray-900">Bangkok</p>
                  <p className="text-xs text-gray-400 tracking-wider mt-1">Spécialisation</p>
                </div>
                <div>
                  <p className="font-serif text-2xl text-gray-900">Couleur</p>
                  <p className="text-xs text-gray-400 tracking-wider mt-1">Expertise</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CITATION ── */}
      <section ref={quoteRef} className="py-28 md:py-36 px-6 bg-stone-50">
        <div className={`max-w-3xl mx-auto text-center transition-all duration-[1.5s] ${quoteVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-serif text-6xl text-gray-200 mb-4">"</p>
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-gray-900 italic font-light leading-relaxed mb-8">
            Chaque bijou devient une présence intime,
            à porter et à transmettre.
          </p>
          <div className="flex items-center justify-center gap-4">
            <div className="w-12 h-px bg-gray-200" />
            <p className="text-xs tracking-[0.4em] uppercase font-light text-gray-400">Sylvie</p>
            <div className="w-12 h-px bg-gray-200" />
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs tracking-[0.5em] uppercase mb-6 font-light text-gray-400">Rendez-vous</p>
          <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6">
            Envie de créer ensemble ?
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mb-10">
            Prenez rendez-vous pour échanger sur votre projet et donner vie à votre création.
          </p>
          <Link to="/contact"
            className="inline-block px-12 py-5 bg-black text-white text-[11px] tracking-[0.3em] uppercase transition-all duration-500 hover:bg-gray-800">
            Nous contacter
          </Link>
        </div>
      </section>
    </div>
  );
}
